import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ensureCsrf, noAnswer } from "./lib/api.js";
import { flushRounds } from "./lib/outbox.js";
import { forgetOffline } from "./lib/offline.js";

export interface SessionUser {
  id: string;
  handle: string;
  display_name: string;
  role: "learner" | "adult" | "operator";
}

export interface SessionClass {
  id: string;
  name: string;
  role: "member" | "admin";
}

interface MePayload {
  user: SessionUser;
  class: SessionClass | null;
}

interface SessionValue {
  ready: boolean;
  /** No connection: the session shown is the one this device remembers (issue #118). */
  offline: boolean;
  user: SessionUser | null;
  membership: SessionClass | null;
  setUser: (user: SessionUser | null) => void;
  refresh: () => Promise<void>;
}

const Session = createContext<SessionValue | null>(null);

/**
 * The last /api/me on this device, so the app opens without a connection
 * (issue #118) instead of on the login. Nothing the app does not show anyway:
 * nickname, role, the class name. Gone on logout, on account deletion and when
 * the worker says the session is no longer valid.
 */
const REMEMBERED = "vf_me";

function remember(data: MePayload | null): void {
  try {
    if (data) localStorage.setItem(REMEMBERED, JSON.stringify(data));
    else {
      localStorage.removeItem(REMEMBERED);
      // The rounds and the Start kept for practising offline (issue #120) go with it.
      forgetOffline();
    }
  } catch {
    // Storage refused (a private window): the app then needs a connection to start.
  }
}

function remembered(): MePayload | null {
  try {
    const raw = localStorage.getItem(REMEMBERED);
    return raw ? (JSON.parse(raw) as MePayload) : null;
  } catch {
    return null;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [membership, setMembership] = useState<SessionClass | null>(null);
  const [offline, setOffline] = useState(false);

  const refresh = async () => {
    await ensureCsrf();
    try {
      const data = await api<MePayload>("/api/me");
      setUser(data.user);
      setMembership(data.class);
      setOffline(false);
      // Rounds that waited for a connection go out now.
      void flushRounds(data.user.id);
    } catch (err) {
      // Only a missing connection keeps the remembered session. A 401 means
      // the session is over (logged out on another device, say): showing the
      // child as logged in would leave every request failing, with no way out.
      const kept = noAnswer(err) ? remembered() : null;
      if (!kept) remember(null);
      setUser(kept?.user ?? null);
      setMembership(kept?.class ?? null);
      setOffline(kept !== null);
    } finally {
      setReady(true);
    }
  };

  // Whatever the worker last confirmed, also after login or registration,
  // which set the user without asking /api/me. Not while offline: then the
  // session shown is the remembered one, not a confirmed one.
  useEffect(() => {
    if (!ready || offline) return;
    remember(user ? { user, class: membership } : null);
  }, [ready, offline, user, membership]);

  useEffect(() => {
    void refresh();
    // Back online: check the session for real and send what waited.
    const online = () => void refresh();
    const gone = () => setOffline(true);
    window.addEventListener("online", online);
    window.addEventListener("offline", gone);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", gone);
    };
  }, []);

  const value = useMemo(
    () => ({
      ready,
      offline,
      user,
      membership,
      setUser: (next: SessionUser | null) => {
        setUser(next);
        if (!next) {
          setMembership(null);
          // Also while offline, when the effect above does not write.
          remember(null);
          setOffline(false);
        }
      },
      refresh,
    }),
    [ready, offline, user, membership],
  );
  return <Session.Provider value={value}>{children}</Session.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(Session);
  if (!value) throw new Error("SessionProvider missing");
  return value;
}
